import React, { FC, useEffect, useState } from 'react';
import {
  Typography,
  FormControl,
  Grid,
  Button,
  SelectChangeEvent,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  FormControlLabel,
  Avatar,
} from '@mui/material';
import { RoleId, Roles, User, UserModules } from '@models/User';
import { useUpdateUserMutation, useVerifyUserMutation, useDeleteUserMutation } from '@services/users/users';
import { useAppDispatch, useAppSelector } from '@hooks/redux-custom';
import { showToast } from '@store/slices/notification';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import AlternateEmail from '@mui/icons-material/AlternateEmail';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { JimboResponse } from '@models';
import { TwoFactorAuthenticationModal } from '@components';
import { Mood, MoodBad } from '@mui/icons-material';

interface Props {
  user: User;
  modules: UserModules;
}

enum Role {
  Customer = 'customer',
  Product = 'product',
  Ava = 'ava',
  Webhook = 'webhook',
  Template = 'templates',
  User = 'users',
  Campaign = 'campaign',
}

export const UserManagement: FC<Props> = ({ user, modules }) => {
  const dispatch = useAppDispatch();
  const [userRoleUpdates, setUserRoleUpdates] = useState<Roles>([]);
  const [facode, setFacode] = useState('');
  const [isErrorCode, setIsErrorCode] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [currentModule, setCurrentModule] = useState<string>(Role.Customer);

  const [updateUser] = useUpdateUserMutation();
  const [verifyUser] = useVerifyUserMutation();
  const [deleteUser] = useDeleteUserMutation();

  const activeUser = useAppSelector((state) => state.activeUser.value);

  const userAccessStatus = activeUser?.user?.roles.find((role: any) => role.moduleName === 'users');
  const hasWriteAccess = userAccessStatus.role > 1;

  useEffect(() => user.roles && setUserRoleUpdates(user.roles), [user]);

  const handleSelectChange = (role: RoleId, moduleName: string) => {
    setUserRoleUpdates([
      ...userRoleUpdates.filter((m) => m.moduleName !== moduleName),
      {
        moduleName: moduleName,
        role: role,
      },
    ]);
  };

  const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (userRoleUpdates !== user.roles) {
      updateUser({ ...user, roles: userRoleUpdates })
        .unwrap()
        .then(() => {
          dispatch(
            showToast({
              message: `Updated ${user.firstName}'s role successfully!`,
              type: 'success',
            }),
          );
        })
        .catch(() => {
          dispatch(
            showToast({
              message: 'Failed to update.',
              type: 'error',
            }),
          );
        });
    }
  };

  const verify2FA = async (evt: { preventDefault: () => void }) => {
    evt.preventDefault();

    try {
      setIsErrorCode(false);
      const value: any = await verifyUser({
        email: activeUser.user.email,
        code: facode,
      });

      if (value?.data?.code === 200) {
        setModalOpen(false);

        deleteUser({ userId: user.id || '' })
          .unwrap()
          .then(() => {
            dispatch(
              showToast({
                message: 'User successfully deleted!',
                type: 'success',
              }),
            );
          })
          .catch((error: { data: JimboResponse }) => {
            dispatch(
              showToast({
                message: 'Failed to delete User!',
                type: 'error',
              }),
            );
          });
      } else {
        setIsErrorCode(true);
      }
    } catch (err) {
      setIsErrorCode(true);
      console.log('verify2FA error', err);
    }
  };

  const openCloseAccordion = (panel: string) => {
    setCurrentModule((prePanel) => (prePanel !== panel ? panel : ''));
  };

  return (
    <Box border="1px solid lightgrey" borderRadius="4px" overflow="auto" flex={1} minWidth="500px">
      <Box display="flex" flexDirection="column" justifyContent="center" gap={4} px={4} my={2}>
        <Box display="flex" gap={2} borderBottom="1px solid lightgrey" pb={1}>
          <Box display="flex" alignItems="center">
            <List dense>
              <ListItem>
                <ListItemIcon>
                  <PersonOutlineOutlinedIcon />
                </ListItemIcon>
                <ListItemText primary={`${user.firstName} ${user.lastName}`} />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <AlternateEmail />
                </ListItemIcon>
                <ListItemText primary={user.email} />
              </ListItem>
              <ListItem>
                <ListItemIcon>{user.isActive ? <CheckCircleIcon /> : <CancelIcon />}</ListItemIcon>
                <ListItemText primary={user.isActive ? 'Active' : 'Inactive'} />
              </ListItem>
            </List>
          </Box>
          <Box display="flex" alignItems="center" flex={1} justifyContent="center">
            <Typography variant="caption">Mark a User as Admin by setting Users role to 'Write'</Typography>
          </Box>
        </Box>
        <Box height="100%">
          <form onSubmit={handleFormSubmit} data-testid="user-role-form">
            <Grid container spacing={2}>
              {modules.map((module) => {
                const userModule = userRoleUpdates.find((role) => role.moduleName === module.name);
                return (
                  <FormControl fullWidth>
                    <Accordion expanded={currentModule === module.name} onChange={() => openCloseAccordion(module.name)}>
                      <AccordionSummary>
                        <Typography>{module.name}</Typography>
                        <Avatar sx={{ background: 'transparent' }}>
                          {userModule?.role !== RoleId.None ? (
                            <Mood fontSize="small" color="success" />
                          ) : (
                            <MoodBad fontSize="small" color="error" />
                          )}
                        </Avatar>
                      </AccordionSummary>
                      <AccordionDetails>
                        <FormControlLabel
                          label={'Read'}
                          control={
                            <Checkbox
                              checked={userModule?.role !== RoleId.None}
                              onChange={(event: SelectChangeEvent<RoleId>) =>
                                handleSelectChange(
                                  userModule?.role !== RoleId.None ? (RoleId.None as RoleId) : (RoleId.Read as RoleId),
                                  module.name,
                                )
                              }
                              data-testid="read-checkbox"
                            />
                          }
                          sx={{ display: 'block' }}
                        />

                        <FormControlLabel
                          label={'Write'}
                          control={
                            <Checkbox
                              checked={(userModule?.role ?? RoleId.None) > RoleId.Read}
                              onChange={(event: SelectChangeEvent<RoleId>) =>
                                handleSelectChange(
                                  userModule?.role === RoleId.Write ? (RoleId.Read as RoleId) : (RoleId.Write as RoleId),
                                  module.name,
                                )
                              }
                            />
                          }
                          sx={{ display: 'block' }}
                          data-testid="write-checkbox"
                        />

                        {module.name === Role.Customer && (
                          <>
                            <FormControlLabel
                              label={'Reset Firebase Record'}
                              control={
                                <Checkbox
                                  checked={(userModule?.role ?? RoleId.None) > RoleId.Write}
                                  onChange={(event: SelectChangeEvent<RoleId>) =>
                                    handleSelectChange(
                                      userModule?.role === RoleId.ResetFirebaseRecord
                                        ? (RoleId.Write as RoleId)
                                        : (RoleId.ResetFirebaseRecord as RoleId),
                                      module.name,
                                    )
                                  }
                                />
                              }
                              sx={{ display: 'block' }}
                              data-testid="reset-firebase-record-checkbox"
                            />
                            <FormControlLabel
                              label={'Terminate Customer'}
                              control={
                                <Checkbox
                                  checked={(userModule?.role ?? RoleId.None) > RoleId.ResetFirebaseRecord}
                                  onChange={(event: SelectChangeEvent<RoleId>) =>
                                    handleSelectChange(
                                      userModule?.role === RoleId.TerminateCustomer
                                        ? (RoleId.ResetFirebaseRecord as RoleId)
                                        : (RoleId.TerminateCustomer as RoleId),
                                      module.name,
                                    )
                                  }
                                />
                              }
                              sx={{ display: 'block' }}
                              data-testid="terminate-customer-checkbox"
                            />
                            <FormControlLabel
                              label={'View Logs'}
                              control={
                                <Checkbox
                                  checked={(userModule?.role ?? RoleId.None) > RoleId.TerminateCustomer}
                                  onChange={(event: SelectChangeEvent<RoleId>) =>
                                    handleSelectChange(
                                      userModule?.role === RoleId.ViewUserLogs
                                        ? (RoleId.TerminateCustomer as RoleId)
                                        : (RoleId.ViewUserLogs as RoleId),
                                      module.name,
                                    )
                                  }
                                />
                              }
                              sx={{ display: 'block' }}
                              data-testid="view-activity-log-checkbox"
                            />
                            <FormControlLabel
                              label={'Terminate without OTP'}
                              control={
                                <Checkbox
                                  checked={(userModule?.role ?? RoleId.None) > RoleId.ViewUserLogs}
                                  onChange={(event: SelectChangeEvent<RoleId>) =>
                                    handleSelectChange(
                                      userModule?.role === RoleId.TerminateCustomerWithoutOTP
                                        ? (RoleId.ViewUserLogs as RoleId)
                                        : (RoleId.TerminateCustomerWithoutOTP as RoleId),
                                      module.name,
                                    )
                                  }
                                />
                              }
                              sx={{ display: 'block' }}
                              data-testid="terminate-customer-without-OTP-checkbox"
                            />
                          </>
                        )}
                        {module.name === Role.Product && (
                          <>
                            <FormControlLabel
                              label={'Product Sync'}
                              control={
                                <Checkbox
                                  checked={userModule?.role == RoleId.ProductSync}
                                  onChange={(event: SelectChangeEvent<RoleId>) =>
                                    handleSelectChange(
                                      userModule?.role == RoleId.ProductSync ? (RoleId.Write as RoleId) : (RoleId.ProductSync as RoleId),
                                      module.name,
                                    )
                                  }
                                />
                              }
                              sx={{ display: 'block' }}
                              data-testid="product-sync-checkbox"
                            />
                          </>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  </FormControl>
                );
              })}
            </Grid>
            {hasWriteAccess && (
              <>
                <Grid mt={2} container spacing={2}>
                  <Grid item xs={12} sm={6} />
                  <Grid item xs={12} sm={3} justifyContent="flex-end">
                    <Button variant="contained" type="submit" data-testid="user-role-submit-button" fullWidth>
                      Update Roles
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={3} justifyContent="flex-end">
                    <Button
                      onClick={() => {
                        setModalOpen(true);
                        setFacode('');
                        setIsErrorCode(false);
                      }}
                      variant="contained"
                      color="error"
                      type="submit"
                      data-testid="delete-user-button"
                      fullWidth
                    >
                      Remove User
                    </Button>
                  </Grid>
                </Grid>
                <TwoFactorAuthenticationModal
                  isErrorCode={isErrorCode}
                  onSubmit={verify2FA}
                  open={modalOpen}
                  onClose={() => setModalOpen(false)}
                  fieldValue={facode}
                  onInputChange={(e) => setFacode(e.target.value)}
                  modalDescription={'Please enter the 2FA code below to remove this user'}
                  errorDesc={'Invalid 2FA code. Please try again.'}
                />
              </>
            )}
          </form>
        </Box>
      </Box>
    </Box>
  );
};
